/**
 * Trade Hub — /friends/:id/trades
 *
 * Trading with one friend, as three pages side by side in the order a trade
 * actually moves: Potential → Proposed → Completed. Swipe between them, or tap
 * the segmented strip under each page's heading.
 *
 * ONE ROUTE, THREE PAGES. The panes share nearly all of their data (the trade
 * list feeds both Potential's centrepiece and Proposed's Trade Match; agreeing
 * to a proposal moves a row from Proposed to Completed), so they sit under one
 * route with one data owner — see useTradeHubData. Splitting them into three
 * routes would mean three copies of the same fetches disagreeing with each
 * other for a beat after every action.
 *
 * The pane is still addressable: `?pane=proposed` deep-links straight to it, and
 * selecting a pane rewrites the query string, so the in-app notification links
 * that point at a specific stage of a trade can keep working.
 *
 * RELATIONSHIP TO COMPARE. /friends/:id/compare is now head-to-head ONLY — the
 * Trade Match, proposals, trade list, lead panels and filter wheel it used to
 * carry were removed there when they landed here, so neither page duplicates
 * the other.
 */
import { useEffect, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { ArrowLeftRight } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { scrollPageTo } from "@/lib/pageScroll";
import { SwipePager } from "@/components/trade/SwipePager";
import {
  TRADE_PANES,
  TradeHubTabs,
  tradeTabId,
  type TradePaneKey,
} from "@/components/trade/TradeHubTabs";
import { useTradeHubData } from "@/pages/trades/useTradeHubData";
import { PotentialTradesPane } from "@/pages/trades/PotentialTradesPane";
import { ProposedTradesPane } from "@/pages/trades/ProposedTradesPane";
import { CompletedTradesPane } from "@/pages/trades/CompletedTradesPane";

const PANE_KEYS = TRADE_PANES.map((p) => p.key);

/** Read `?pane=` out of either a hash route or a real query string. */
function paneFromLocation(): TradePaneKey | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash;
  const raw = hash.includes("?") ? hash.slice(hash.indexOf("?")) : window.location.search;
  const value = new URLSearchParams(raw).get("pane");
  return value && (PANE_KEYS as string[]).includes(value) ? (value as TradePaneKey) : null;
}

export default function TradeHub() {
  const [, params] = useRoute<{ id: string }>("/friends/:id/trades");
  const [, setLocation] = useLocation();

  const friendId = params?.id && /^\d+$/.test(params.id) ? Number(params.id) : null;
  const [index, setIndex] = useState(() => {
    const from = paneFromLocation();
    return from ? PANE_KEYS.indexOf(from) : 0;
  });

  const hub = useTradeHubData(friendId);

  // Keep the URL honest about which pane is showing, without adding a history
  // entry per swipe — swiping back and forth should not bury the Back button.
  useEffect(() => {
    if (typeof window === "undefined" || friendId == null) return;
    const key = PANE_KEYS[index];
    const url = new URL(window.location.href);
    if (key === "potential") url.searchParams.delete("pane");
    else url.searchParams.set("pane", key);
    window.history.replaceState(window.history.state, "", url.toString());
  }, [index, friendId]);

  /**
   * Changing pane returns you to the top of it.
   *
   * The panes are wildly different lengths — Potential runs to thousands of
   * leads, Completed is often a handful of rows — but the PAGE scroll is shared
   * between them, because they are one route. So swiping across while scrolled
   * a long way down Potential dropped you past the end of Proposed, staring at
   * blank space and having to scroll back up to find the page you had just
   * asked for. Reported on device 2026-08-12.
   *
   * Instant, not smooth: this is a new page arriving, not a movement within the
   * one you are on, and animating a long scroll fights the pane's own slide.
   */
  const setPane = (next: number) => {
    setIndex(next);
    if (next !== index) scrollPageTo(0);
  };
  const goToPane = (key: TradePaneKey) => setPane(PANE_KEYS.indexOf(key));

  /* ── unknown friend ── */

  if (friendId == null || (!hub.friend && !hub.friendLoading)) {
    return (
      <AppShell title="Trades">
        <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
          <div className="v2-neon-card p-8 w-full max-w-xs" data-testid="trade-hub-unknown">
            <div
              className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ background: "var(--surface-dark-10)", border: "1.5px solid var(--accent-fill-glow)" }}
            >
              <ArrowLeftRight className="w-6 h-6" style={{ color: "var(--accent-fill)" }} />
            </div>
            <h2 className="v2-display text-base mb-2" style={{ color: "var(--text-ink-bright)" }}>
              Friend not found
            </h2>
            <p className="text-xs mb-5" style={{ color: "var(--text-ink-faint)" }}>
              Trading needs a connected friend. Head to your friends list to pick one.
            </p>
            <button
              onClick={() => setLocation("/friends")}
              className="v2-neon-cta w-full py-3 text-sm"
              data-testid="button-trade-hub-go-friends"
            >
              Go to Friends
            </button>
          </div>
        </div>
      </AppShell>
    );
  }

  // Counts live on the tabs so a glance says whether the pane you are NOT on has
  // anything waiting. null while loading — a zero that later changes is worse
  // than no number at all.
  const counts = {
    potential: hub.compareQ.isLoading ? null : hub.yourFiltered.length + hub.theirFiltered.length,
    proposed: hub.liveProposalsQ.isLoading ? null : hub.liveProposals.length,
    completed: hub.historyQ.isLoading ? null : hub.history.length,
  };
  // Each pane renders its OWN strip under its own heading, so the strip travels
  // with the page. Only the pane on screen gets the live one — see the note on
  // TradeHubTabs' `interactive` prop.
  const tabsFor = (owner: TradePaneKey) => (
    <TradeHubTabs
      active={PANE_KEYS[index]}
      onSelect={goToPane}
      counts={counts}
      interactive={owner === PANE_KEYS[index]}
    />
  );

  return (
    <AppShell pageSpacing>
      <div className="relative z-10 pb-safe">
        {/* -mx-4, MARGINS ONLY (2026-08-21 second pass): the pager clips X at
            its own box, which cut the panes' pinned bands 1rem short of the
            screen edges. The first fix used sticky-band-bleed (margin +
            padding), but the padding narrowed the pane track, so neighbouring
            panes poked into the gutters. Negative margins alone put the clip
            boundary AND the track at the full screen width — neighbours sit
            exactly offscreen — and each pane re-adds the column padding
            itself (the px-4 wrappers below), which the bands' own bleed then
            cancels to reach the true edge. */}
        <SwipePager
          className="-mx-4"
          index={index}
          onIndexChange={setPane}
          labelledBy={tradeTabId(PANE_KEYS[index])}
          panes={[
            { key: "potential", node: <div className="px-4"><PotentialTradesPane hub={hub} tabs={tabsFor("potential")} /></div> },
            { key: "proposed", node: <div className="px-4"><ProposedTradesPane hub={hub} tabs={tabsFor("proposed")} goToPane={goToPane} /></div> },
            { key: "completed", node: <div className="px-4"><CompletedTradesPane hub={hub} tabs={tabsFor("completed")} goToPane={goToPane} /></div> },
          ]}
        />
      </div>
    </AppShell>
  );
}
